import { v4 as UUIDv4 } from 'uuid';
import omit from 'lodash/omit';
import { gzip } from 'pako/lib/deflate';
import { inflate } from 'pako/lib/inflate';
import throttle from 'lodash/throttle';
import { notifyError } from '@shakkerai/pro-components';
import { requestCloudData, updateCanvasData } from '@shakkerai/services';
import { defaultHiresOptions, samplerMethodOptions, vaeOptions } from '@shakkerai/pro-constants';
import { getImageSegmentCacheKey, resumeInPorgressTasks } from '@shakkerai/pro-utils';
import { MODEL_SDXL, SDXL_MODELS_FAMILY } from '@shakkerai/pro-models';
import { getCanvasState, setCanvasState } from '../state/canvas';
import { initialCoreData, initialTaskData, initialGenerator } from '../state/canvas';
import { getLoadings } from '../state/loadings';
import { useUserInfo } from '../state/auth';
import { useSegments } from '../state/segments';
import { getImageOssPath } from '@shakkerai/util-image';
import { initialGeneratorParams } from '@shakkerai/pro-constants';

const idleStatus = { idle: true, success: true, error: true };

export const windowId = UUIDv4();

const captialize = (str) => str.charAt(0).toUpperCase() + str.slice(1);
const stripUrlToken = (url) => url.replace(/\?Token=[^&]+/, '');
const changeUrlHost = (url) => {
  if (url && typeof url === 'string') {
    return url.replace('.vibrou.com', '.liblib.cloud');
  }
  return url;
};

const fixReferences = (params) => {
  if (!params) return;

  params.controlnets = params.controlnets || [];

  if (params.scale) {
    params.strength = params.scale;
    delete params.scale;
  }

  // 兼容旧的生成器数据
  // @since 0.9.8/202407
  const referenceCNItem = params.controlnets.find((item) => item.type === '"reference"');

  if (referenceCNItem) {
    if (referenceCNItem.sourceImage) {
      params.imageReference = {
        id: referenceCNItem.sourceImage.id,
        url: changeUrlHost(referenceCNItem?.sourceImage?.url),
        width: referenceCNItem.sourceImage.width,
        height: referenceCNItem.sourceImage.height,
        strength: referenceCNItem.controlWeight,
      };
    }

    params.controlnets = params.controlnets.filter((item) => item.type !== '"reference"');
  }

  // 兼容旧的生成器数据
  // @since 0.9.5/202402
  if (params.reference) {
    params.strength = params.reference.scale || 50;

    if (params.reference.image) {
      params.controlnets = params.controlnets
        .filter((item) => item.enabled)
        .map((item) => {
          item.sourceImage = params.reference.image;
          item.id = item.id || UUIDv4();
          return item;
        });

      // params.controlnets.unshift({
      //   type: 'reference',
      //   enabled: true,
      //   sourceImage: params.reference.image,
      //   controlWeight: params.reference.scale,
      // });
    } else {
      params.controlnets = [];
    }
  }

  delete params.reference;
  return params;
};

const fixParamsHost = (params) => {
  if (!params) return;
  const { loraModels, style, checkpoint } = params;
  if (loraModels && loraModels.length > 0) {
    loraModels.forEach((lora) => {
      if (lora.imageUrl) {
        lora.imageUrl = changeUrlHost(lora.imageUrl);
      }
      if (lora.userAvatar) {
        lora.userAvatar = changeUrlHost(lora.userAvatar);
      }
    });
  }
  if (style && style.imageUrl) {
    style.imageUrl = changeUrlHost(style.imageUrl);
  }
  if (checkpoint && checkpoint.imageUrl) {
    checkpoint.imageUrl = changeUrlHost(checkpoint.imageUrl);
  }
};

export const fixCloudData = (cloudData) => {
  const taskMap = cloudData.taskMap || {};

  Object.keys(taskMap).forEach((key) => {
    if (taskMap[key].status === 'pending' && !taskMap[key].taskId) {
      taskMap[key].status = 'idle';
    }

    if (taskMap[key].hasSubTasks) {
      if (!taskMap[key].images || taskMap[key].images.every((item) => idleStatus[item.status] || !item.taskId)) {
        taskMap[key].status = 'idle';
        taskMap[key].taskId = null;
        taskMap[key].error = null;
      }
    }

    fixReferences(taskMap[key].params);
  });

  if (cloudData.generators) {
    cloudData.generators = cloudData.generators
      .filter((item) => item.id)
      .map((item) => {
        if (!item.id.startsWith('generator')) {
          item.id = 'generator' + captialize(item.type) + item.id;
        }

        if (!item.params) {
          item.params = { ...initialGenerator.params };
        }

        if (item.type === 'primary') {
          // 只有非刷新页面的情况下才会重置生成器参数
          if (!sessionStorage.getItem('shakker_should_keep_generator_params')) {
            item.params = Object.assign({}, initialGeneratorParams.primary);
          }

          sessionStorage.setItem('shakker_should_keep_generator_params', '1');

          // 尝试修复hiresOptions
          item.params.hiresOptions = Object.assign({}, defaultHiresOptions, item.params.hiresOptions);
        }

        if (item.type === 'inpaint') {
          item.params.baseType = MODEL_SDXL;
          item.params.checkpoint = null;
          item.params.loraModels = [];
          item.params.addOns = [];
        }

        if (item.params.style) {
          delete item.params.style;
        }

        delete item.preview;
        delete item.previewEnabled;

        fixReferences(item.params);
        fixParamsHost(item.params);

        if (!samplerMethodOptions.some((option) => option.value === item.params.samplerMethod)) {
          item.params.samplerMethod = initialGenerator.params.samplerMethod;
        }

        if (SDXL_MODELS_FAMILY[item.params.baseType]) {
          item.params.vae = '-1';
        } else if (!vaeOptions.some((option) => option.value === item.params.vae)) {
          item.params.vae = initialGenerator.params.vae;
        }

        if (item.params.seedType !== '0' && item.params.seedType !== '1') {
          item.params.seedType = initialGenerator.params.seedType;
        }

        if (!item.params.controlnets.length) {
          item.params.controlnets = initialGenerator.params.controlnets.slice();
        }

        if (item.params.loraModels) {
          item.params.loraModels = item.params.loraModels.filter((item) => !!item);
        }

        if (item.result) {
          taskMap[item.id] = item.result;
          delete item.result;
        }

        // 如果任务没有任务ID，则初始化为新任务
        if (taskMap[item.id] && !taskMap[item.id].taskId) {
          taskMap[item.id] = { ...initialTaskData };
        }

        // 如果是主生成器，但是没有任务数据，则填充默认任务数据
        if (item.type === 'primary' && !taskMap[item.id]) {
          taskMap[item.id] = { ...initialTaskData };
        }

        // 修正尺寸
        item.params.width = Math.round(item.params.width);
        item.params.height = Math.round(item.params.height);

        // 取出参考图的controlnet预处理图放到缓存里面
        if (item.params.controlnets) {
          item.params.controlnets.forEach((controlnet) => {
            if (typeof controlnet.startingControlStep !== 'number') {
              controlnet.startingControlStep = 0;
              controlnet.endingControlStep = 1;
            }

            if (typeof controlnet.pixelPerfect === 'undefined') {
              controlnet.pixelPerfect = '1';
            }

            if (controlnet.controlWeight && controlnet.controlWeight > 1) {
              controlnet.controlWeight = Number((controlnet.controlWeight / 100).toFixed(2));
            }
          });
        }

        return item;
      });

    cloudData.taskMap = taskMap;

    // 删除无效的图片工具生成器
    const inpaintGeneratorsMap = Object.fromEntries(
      cloudData.elements.filter((item) => !!item.inpaint?.generatorId).map((item) => [item.inpaint.generatorId, true]),
    );

    const outpaintGeneratorMap = Object.fromEntries(
      cloudData.elements.filter((item) => !!item.outpaint).map((item) => [item.outpaint, true]),
    );

    const collageGeneratorMap = Object.fromEntries(
      cloudData.elements.filter((item) => !!item.collage).map((item) => [item.collage, true]),
    );

    const upscaleGeneratorMap = Object.fromEntries(
      cloudData.elements.filter((item) => !!item.upscaler).map((item) => [item.upscaler, true]),
    );

    const changeFaceGeneratorsMap = Object.fromEntries(
      cloudData.elements.filter((item) => !!item.changeFace).map((item) => [item.changeFace, true]),
    );

    cloudData.elements.forEach((item) => {
      delete item.transfer;
      delete item.remix;
    });

    cloudData.generators = cloudData.generators.filter((item) => {
      if (item.type === 'primary') return true;
      if (item.type === 'collage-elements') return true;

      return (
        inpaintGeneratorsMap[item.id] ||
        collageGeneratorMap[item.id] ||
        upscaleGeneratorMap[item.id] ||
        outpaintGeneratorMap[item.id] ||
        changeFaceGeneratorsMap[item.id]
      );
    });
  }

  const deletedCollageGeneratorIds = {};

  if (cloudData.taskMap) {
    Object.keys(cloudData.taskMap).forEach((key) => {
      if (cloudData.taskMap[key].status === 'pending' && !cloudData.taskMap[key].taskId) {
        cloudData.taskMap[key].status = 'idle';
      }

      if (cloudData.taskMap[key].status === 'failed') {
        cloudData.taskMap[key].status = 'error';
      }

      if (cloudData.taskMap[key].status === 'success') {
        if (Array.isArray(cloudData.taskMap[key].images)) {
          cloudData.taskMap[key].images = cloudData.taskMap[key].images.map((item) => {
            if (item.url) item.url = changeUrlHost(stripUrlToken(item.url));
            return item;
          });
        }
      }

      if (cloudData.taskMap[key].status === 'idle') {
        if (!cloudData.generators.some((item) => item.id === key)) {
          delete cloudData.taskMap[key];
        }

        if (key.startsWith('generatorCollageElements')) {
          deletedCollageGeneratorIds[key] = true;
          delete cloudData.taskMap[key];
        }
      }
    });
  }

  if (cloudData.generators) {
    cloudData.generators = cloudData.generators.filter((item) => {
      if (item.type === 'collage-elements') {
        return !deletedCollageGeneratorIds[item.id];
      }

      return true;
    });
  }

  Object.keys(initialCoreData).forEach((key) => {
    if (!Object.prototype.hasOwnProperty.call(cloudData, key)) {
      cloudData[key] = initialCoreData[key];
    }
  });

  if (cloudData.elements) {
    cloudData.elements.forEach((element) => {
      element.t = element.t || Date.now();

      // 先不删出，留着等正式上线之后再删除
      // delete element.generateInfo;
      // delete element.pngInfo;
      // delete element.seed;
      // delete element.acturalSeed;
      // delete element.imageInfo;

      if (element.url) element.url = changeUrlHost(element.url);
      if (element.originalUrl) element.originalUrl = changeUrlHost(element.originalUrl);
    });
  }

  if (!cloudData.generators.length) {
    cloudData.generators = [initialGenerator];
  }

  if (!cloudData.generators[0].params.count) {
    cloudData.generators[0] = { ...initialGenerator };
  }

  cloudData.activeCrop = null;
  cloudData.activeRemove = null;
  cloudData.activeSmartRemove = null;

  return cloudData;
};

let prevSaveData = null;
// const bc = new BroadcastChannel('canvas-data-update');

export const initCloudData = async () => {
  const { userInfo } = useUserInfo.getState();
  const { startLoading, stopLoading } = getLoadings();

  if (!userInfo?.id) return;

  startLoading('canvas-data-fetching');

  const localSaveKey = userInfo.id + '-local-canvas-data';
  const localData = localStorage.getItem(localSaveKey);
  const localVersion = localStorage.getItem(localSaveKey + '-version');

  try {
    const response = await requestCloudData();

    if (response.code !== 0) throw new Error(response.msg);

    let cloudData = null;

    if (response.data) {
      try {
        if (response.data.startsWith('SHAKKERDATA://')) {
          const compressedData = response.data.slice('SHAKKERDATA://'.length);
          const base64decodedData = atob(compressedData);
          const uint8Array = new Uint8Array(base64decodedData.length);

          for (let i = 0; i < base64decodedData.length; i++) {
            uint8Array[i] = base64decodedData.charCodeAt(i);
          }

          cloudData = JSON.parse(inflate(uint8Array, { to: 'string' }));
        } else {
          cloudData = JSON.parse(response.data);
        }
      } catch (error) {
        console.log(error);
        // noop
      }
    }

    if (cloudData) {
      if (localData && cloudData.__version && cloudData.__version <= localVersion) {
        cloudData = JSON.parse(localData);
        localStorage.removeItem(localSaveKey);
        localStorage.removeItem(localSaveKey + '-version');
      }

      const { ...canvasCoreData } = cloudData;

      canvasCoreData.initialized = true;
      canvasCoreData.isGuestData = false;
      canvasCoreData.__skipSaving = true;

      const data = fixCloudData(canvasCoreData);

      try {
        const cachedSegments = localStorage.getItem('shakker-image-segments');

        if (cachedSegments) {
          const segments = JSON.parse(cachedSegments);

          const inpaintIdsMap = Object.fromEntries(
            data.elements
              .filter((item) => !!item.url)
              .map((item) => {
                const cacheKey = getImageSegmentCacheKey(getImageOssPath(item.url), 'inpaint');
                return [cacheKey, true];
              }),
          );

          const changeFaceIdsMap = Object.fromEntries(
            data.elements
              .filter((item) => !!item.url)
              .map((item) => {
                const cacheKey = getImageSegmentCacheKey(getImageOssPath(item.url), 'changeFace');
                return [cacheKey, true];
              }),
          );

          const elementsIdsMap = Object.assign({}, inpaintIdsMap, changeFaceIdsMap);

          let hasInvalidSegments = false;

          Object.keys(segments).forEach((key) => {
            if (!elementsIdsMap[key]) {
              hasInvalidSegments = true;
              delete segments[key];
            }
          });

          if (hasInvalidSegments) {
            localStorage.setItem('shakker-image-segments', JSON.stringify(segments));
          }

          useSegments.getState().initItems(segments);
        }
      } catch {
        // noop
      }

      setCanvasState(data);
      resumeInPorgressTasks(data.taskMap);
    } else {
      setCanvasState({ initialized: true, error: null, isGuestData: false, __skipSaving: true });
    }
  } catch (error) {
    setCanvasState({
      error: error.message || error.msg || error,
      initialized: true,
      isGuestData: false,
      __skipSaving: true,
    });
    // notifyError('画布数据获取失败', error.message || error.msg || error);
  }

  stopLoading('canvas-data-fetching');
};

export const resetCanvasState = () => {
  setCanvasState(Object.assign({}, initialCoreData, { initialized: true, isGuestData: true }));
};

// 不持久化的画布状态
export const nonPresistedCoreDataKeys = {
  selectionElements: true,
  initialized: true,
  error: true,
  openedSubWindow: true,
  __skipTracking: true,
  __skipSaving: true,
};

// 不持久化的任务状态
export const nonPresistedTaskDataKeys = {};

let lastLocalSaveAt = 0;
let autoSaveTimer = null;

const LOCAL_SAVE_INTERVAL = 2000;

export const saveCloudData = async (forceUpdate = false) => {
  const { userInfo } = useUserInfo.getState();
  const coreData = getCanvasState();

  if (!userInfo?.id) return;
  if (!coreData.initialized) return;
  if (coreData.isGuestData) return;

  const { startLoading, stopLoading } = getLoadings();
  const serializedData = { taskData: {} };

  Object.keys(coreData).forEach((key) => {
    if (typeof coreData[key] !== 'function' && !nonPresistedCoreDataKeys[key]) serializedData[key] = coreData[key];
  });

  const localSaveKey = userInfo.id + '-local-canvas-data';

  // bc.postMessage({
  //   windowId: windowId,
  //   type: 'canvas-data-update',
  //   canvasData: serializedData,
  // });

  if (!forceUpdate) {
    if (Date.now() - lastLocalSaveAt < LOCAL_SAVE_INTERVAL) {
      // 特定时间内只保存到本地
      localStorage.setItem(localSaveKey, JSON.stringify(serializedData));
      localStorage.setItem(localSaveKey + '-version', serializedData.__version);

      autoSaveTimer = setTimeout(() => {
        saveCloudData(true);
      }, LOCAL_SAVE_INTERVAL);

      return;
    }
  }

  clearTimeout(autoSaveTimer);

  lastLocalSaveAt = Date.now();
  localStorage.removeItem(localSaveKey);
  localStorage.removeItem(localSaveKey + '-version');

  startLoading('canvas-data-saving');

  const saveDataWithoutVersion = JSON.stringify(omit(serializedData, ['__version']));

  if (prevSaveData === saveDataWithoutVersion) {
    stopLoading('canvas-data-saving');
    return;
  }

  prevSaveData = saveDataWithoutVersion;

  const rawString = JSON.stringify(serializedData);
  const deflatedData = gzip(rawString);
  const deflatedDataArray = Array.prototype.slice.call(deflatedData);

  let string = '';

  for (let i in deflatedDataArray) {
    string += String.fromCharCode(deflatedDataArray[i]);
  }

  const compressedString = btoa(string);

  await updateCanvasData({ canvas: 'SHAKKERDATA://' + compressedString }).catch((error) => {
    notifyError('画布数据保存失败', error.message || error.msg || error);
  });

  stopLoading('canvas-data-saving');
};

export const throttledSaveCloudData = throttle(() => saveCloudData(), 200);
